import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  ErrorSeverity,
  type ErrorSurface,
  ErrorType,
} from '@/models/analytics/features/errors';

/**
 * Derives a screen name from the current URL pathname.
 * e.g. "/sessions/123" → "sessions", "/settings/integrations" → "settings_integrations"
 */
function getScreenFromPathname(): string {
  if (typeof window === 'undefined') return 'unknown';
  const path = window.location.pathname;
  const segments = path.split('/').filter(Boolean);
  // Take first 2 segments, skip IDs (segments that look like UUIDs or numbers)
  const meaningful = segments.filter(
    (s) => !/^[0-9a-f-]{8,}$/i.test(s) && !/^\d+$/.test(s)
  );
  return meaningful.slice(0, 2).join('_') || 'home';
}

interface TrackErrorViewedOptions {
  surface: ErrorSurface | string;
  screen?: string;
  error_type?: ErrorType | string;
  error_code?: string;
  error_message: string;
  severity?: ErrorSeverity;
  http_status_code?: number;
  endpoint?: string;
}

const MAX_ERROR_MESSAGE_LENGTH = 200;

/**
 * Sanitizes error messages for analytics by truncating to a safe length.
 * Prevents leaking PII, backend details, or unbounded text into analytics.
 */
export function sanitizeErrorMessage(message: string): string {
  const trimmed = message.trim();
  if (trimmed.length <= MAX_ERROR_MESSAGE_LENGTH) return trimmed;
  return `${trimmed.slice(0, MAX_ERROR_MESSAGE_LENGTH)}…`;
}

/**
 * Tracks an `error_viewed` analytics event. Can be called from non-React contexts
 * (e.g. toast functions) by importing the store directly.
 */
export function trackErrorViewed(options: TrackErrorViewedOptions): void {
  try {
    track({
      event_name: TrackingEventName.ErrorViewed,
      props: {
        surface: options.surface,
        screen: options.screen ?? getScreenFromPathname(),
        error_type: options.error_type ?? ErrorType.UNKNOWN,
        error_code: options.error_code,
        error_message: sanitizeErrorMessage(options.error_message),
        severity: options.severity ?? ErrorSeverity.WARNING,
        http_status_code: options.http_status_code,
        endpoint: options.endpoint,
      },
    });
  } catch {
    // Silently fail — analytics should never break the app
  }
}
